import React from 'react';

interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

interface SimpleChartProps {
  data: ChartDataPoint[];
  type?: 'bar' | 'line' | 'area';
  height?: number;
  className?: string;
}

/**
 * Simple Chart Component
 * Basic bar/line chart visualization without external dependencies
 */
const SimpleChart: React.FC<SimpleChartProps> = ({
  data,
  type = 'bar',
  height = 200,
  className = '',
}) => {
  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-gray-400 text-sm">No data available</div>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const range = maxValue - minValue || 1;

  const getBarHeight = (value: number) => {
    return ((value - minValue) / range) * (height - 60);
  };

  const getLineY = (value: number) => {
    return height - 40 - ((value - minValue) / range) * (height - 60);
  };

  return (
    <div className={`relative ${className}`} style={{ height }}>
      {/* Chart Container */}
      <svg width="100%" height={height} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - 40 - ratio * (height - 60);
          return (
            <line
              key={ratio}
              x1="0"
              y1={y}
              x2="100%"
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
              strokeDasharray="4 4"
            />
          );
        })}

        {/* Chart content */}
        {type === 'bar' && (
          <>
            {data.map((point, index) => {
              const barWidth = 100 / data.length - 2;
              const x = (index * 100) / data.length + 1;
              const barHeight = getBarHeight(point.value);
              const y = height - 40 - barHeight;

              return (
                <g key={index}>
                  <rect
                    x={`${x}%`}
                    y={y}
                    width={`${barWidth}%`}
                    height={barHeight}
                    fill={point.color || '#f97316'}
                    rx="4"
                    opacity="0.8"
                  />
                  {point.value > 0 && (
                    <text
                      x={`${x + barWidth / 2}%`}
                      y={y - 5}
                      textAnchor="middle"
                      className="text-xs font-medium fill-gray-700"
                    >
                      {point.value.toLocaleString()}
                    </text>
                  )}
                </g>
              );
            })}
          </>
        )}

        {type === 'line' && (
          <polyline
            points={data
              .map(
                (point, index) =>
                  `${(index * 100) / (data.length - 1) || 0}%,${getLineY(point.value)}`
              )
              .join(' ')}
            fill="none"
            stroke="#f97316"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {type === 'area' && (
          <>
            <polygon
              points={`0,${height - 40} ${data
                .map(
                  (point, index) =>
                    `${(index * 100) / (data.length - 1) || 0}%,${getLineY(point.value)}`
                )
                .join(' ')} ${100}%,${height - 40}`}
              fill="url(#areaGradient)"
              opacity="0.3"
            />
            <defs>
              <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <polyline
              points={data
                .map(
                  (point, index) =>
                    `${(index * 100) / (data.length - 1) || 0}%,${getLineY(point.value)}`
                )
                .join(' ')}
              fill="none"
              stroke="#f97316"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}

        {/* Labels */}
        {data.map((point, index) => {
          const x = (index * 100) / data.length + 50 / data.length;
          return (
            <text
              key={index}
              x={`${x}%`}
              y={height - 10}
              textAnchor="middle"
              className="text-xs fill-gray-600"
            >
              {point.label.length > 8 ? `${point.label.substring(0, 8)}...` : point.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default SimpleChart;


